// Mirrored from pilot-server/config/constants.js — separate repos, kept in step by hand.

/** Hard cap on orders the map view will plot and route in one go. */
export const MAP_ORDER_CAP = 60;

/** Google Routes API intermediate-waypoint limit. Optimize refuses above this. */
export const ROUTES_API_MAX_INTERMEDIATES = 25;

/** Multi-rider auto-plan ceiling. Mirrors AUTO_PLAN_MAX_ROUTES on the server. */
export const AUTO_PLAN_MAX_ROUTES = 10;

/**
 * Fixed per-route colour palette for the Auto-plan map. Each index is a distinct route; pins,
 * route cards and sequence labels all read from the same list so a route's colour is stable.
 * `pin` is the hosted Google teardrop icon (keeps the shape consistent with Manual mode).
 */
export const ROUTE_COLORS = [
  { name: 'Blue', hex: '#1a73e8', pin: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png' },
  { name: 'Orange', hex: '#f57c00', pin: 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png' },
  { name: 'Purple', hex: '#8e24aa', pin: 'http://maps.google.com/mapfiles/ms/icons/purple-dot.png' },
  { name: 'Pink', hex: '#d81b60', pin: 'http://maps.google.com/mapfiles/ms/icons/pink-dot.png' },
  { name: 'Yellow', hex: '#f9a825', pin: 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png' },
  { name: 'Green', hex: '#2e7d32', pin: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png' },
  { name: 'Light blue', hex: '#0097a7', pin: 'http://maps.google.com/mapfiles/ms/icons/ltblue-dot.png' },
  { name: 'Red', hex: '#c62828', pin: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png' },
  { name: 'Teal', hex: '#00897b', pin: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png' },
  { name: 'Brown', hex: '#6d4c41', pin: 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png' },
];

export const getRouteColor = (index) => ROUTE_COLORS[index % ROUTE_COLORS.length];

/** Only orders that have not yet left the store can be put onto a new route. */
export const MAP_ORDER_STATUSES = ['pending', 'confirmed'];

/**
 * Page size for the map's order fetch. The existing GET /orders endpoint paginates, and the
 * map needs the whole matching set for one delivery date — comfortably above the 60 cap so
 * an over-cap result is still detected rather than silently truncated to the cap.
 */
export const ORDERS_FETCH_LIMIT = 200;

export const DELIVERY_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const isValidDeliveryDate = (value) => {
  if (typeof value !== 'string' || !DELIVERY_DATE_PATTERN.test(value)) {
    return false;
  }
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
};

/**
 * Valid delivery pin for the map. Rejects null/missing and the Gulf-of-Guinea
 * default {lat:0,lng:0} that convertToPilotStructure writes on parse failure.
 */
export const isValidLatLng = (latLong) => {
  if (!latLong || typeof latLong !== 'object') return false;
  const { lat, lng } = latLong;
  if (typeof lat !== 'number' || typeof lng !== 'number') return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
};

export const getOrderId = (order) => order?.orderId || order?.id || null;

export const formatDuration = (seconds) => {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
};

export const formatDistance = (meters) => {
  if (meters == null || !Number.isFinite(meters)) return '—';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
};
